import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { GlassCard } from '../../components/GlassCard';
import BrandLogo from '../../components/BrandLogo';
import { Printer, Download, BookOpen, Check, AlertTriangle } from 'lucide-react';
import { arrayToCsvBlob, downloadBlob } from '../../lib/csv';

const PERIODS = ['30d', '90d', 'ytd'];

export default function TrialBalance() {
  const { token, tenant } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api.erpTrialBalance(token, period).then(setData).catch(e => { if (e.status !== 401) toast.show(e.message, 'error'); }).finally(() => setLoading(false));
  }, [token, period, toast]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const exportCsv = () => {
    if (!rows.length) return;
    downloadBlob(arrayToCsvBlob(rows, ['account_name', 'account_type', 'debit_rupees', 'credit_rupees'], ['Account', 'Type', 'Debit', 'Credit']), `trial-balance-${period}-${new Date().toISOString().slice(0,10)}.csv`);
    toast.show('CSV downloaded', 'success');
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="report-trial-balance-page">
      <div className="hidden print:block mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BrandLogo size={32} theme="light" />
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">{tenant?.name || '1000AD'}</h1>
            <p className="text-gray-500 text-xs">Trial Balance ({period.toUpperCase()}) · Generated {new Date().toLocaleDateString('en-IN')}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center">
            <BookOpen size={20} className="text-blue-600" />
          </div>
          <h1 className="text-gray-900 font-extrabold text-xl">Trial Balance</h1>
        </div>
        <div className="flex items-center gap-2">
          {PERIODS.map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`tb-period-${p}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${period === p ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'}`}>
              {p.toUpperCase()}
            </button>
          ))}
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <button onClick={exportCsv} disabled={!rows.length} data-testid="tb-export-csv" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-40"><Download size={14} /> CSV</button>
          <button onClick={() => window.print()} data-testid="tb-print" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-purple text-white text-sm font-semibold hover:opacity-90"><Printer size={14} /> Print</button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : rows.length > 0 ? (
        <div className="space-y-4">
          <GlassCard className="overflow-hidden" data-testid="tb-table-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tb-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">Account</th>
                    <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">Type</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">Debit</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/60">
                      <td className="px-5 py-3 text-gray-900 font-semibold">{r.account_name}</td>
                      <td className="px-5 py-3"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-bold uppercase">{r.account_type}</span></td>
                      <td className="px-5 py-3 text-right text-gray-700 tabular-nums">{r.debit_rupees > 0 ? formatMoney(r.debit_rupees) : '—'}</td>
                      <td className="px-5 py-3 text-right text-gray-700 tabular-nums">{r.credit_rupees > 0 ? formatMoney(r.credit_rupees) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50">
                    <td colSpan={2} className="px-5 py-3 text-gray-900 font-extrabold">TOTALS</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(totals.debit_rupees)}</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(totals.credit_rupees)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </GlassCard>
          <div className="flex items-center gap-2">
            {totals.balances_ok ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold border border-emerald-200"><Check size={14} /> Books balance</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-500 text-xs font-bold border border-red-200"><AlertTriangle size={14} /> Books do not balance</span>
            )}
          </div>
        </div>
      ) : (
        <GlassCard className="p-12 text-center"><p className="text-gray-500">No trial balance data.</p></GlassCard>
      )}
    </div>
  );
}
